import {
  ChangeEventHandler,
  ClipboardEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
} from "react";
import { classNames } from "../utils/classNames";
import { observer } from "mobx-react";
import { deferred, when } from "../utils/functions";

import { useTranslation } from "../stores/RootStoreContext";
import { StringFieldModel } from "../common/commonForms";

export type TextAreaProps = {
  label?: string;
  placeholder?: string;
  name?: string;
  value: string;
  rows?: number;
  autofocus?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  message?: string;
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;
  onPaste?: ClipboardEventHandler<HTMLTextAreaElement>;
};

export function TextArea({
  label,
  placeholder,
  name,
  value,
  rows = 3,
  autofocus,
  disabled,
  readOnly,
  error,
  message,
  onBlur,
  onChange,
  onKeyDown,
  onPaste,
}: TextAreaProps) {
  return (
    <label className={"grow"}>
      {label && <div className={"tw-mb-1"}>{label}</div>}
      <textarea
        className={classNames(
          "tw-block",
          "tw-border",
          "tw-rounded",
          "focus:tw-ring-1",
          "tw-w-full",
          "tw-py-3",
          "tw-px-4",
          error
            ? [
                "color-default",
                "bg-default",
                "border-error",
                "focus-within:border-error",
                "focus-within:ring-error",
                "hover:border-error",
                "hover:focus:border-error",
              ]
            : disabled
              ? ["subtle", "bg-gray-x-soft", "border-gray-soft", "hover:border-gray-soft"]
              : [
                  "color-default",
                  "bg-default",
                  "border-gray",
                  "focus:border-primary",
                  "focus:ring-primary",
                  "hover:bg-border-gray-hard",
                  "hover:focus:border-primary",
                ],
        )}
        name={name}
        placeholder={placeholder}
        value={value}
        autoFocus={autofocus}
        readOnly={readOnly}
        disabled={disabled}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        rows={rows}
      />
      {message && (
        <div
          className={classNames("tw-pt-1", "tw-pl-4", "text-sm", error ? "color-error" : "subtle")}
        >
          {message}
        </div>
      )}
    </label>
  );
}

type MTextAreaProps = {
  model: StringFieldModel;
} & Omit<TextAreaProps, "value">;

export const MTextArea = observer(function MTextInput({
  model,
  onChange,
  onBlur,
  onPaste,
  ...props
}: MTextAreaProps) {
  const { t } = useTranslation();
  return (
    <TextArea
      {...props}
      value={model.inputValue}
      onChange={onChange ?? model.setInputValue}
      onBlur={onBlur ?? model.commit}
      onPaste={onPaste ?? deferred(model.commit)}
      error={!model.isValid}
      message={when(model.error, t)}
    />
  );
});
